import styled, { css } from 'styled-components';

import { size } from '../../layout/helpers';

interface ImageProps {
  $contain?: boolean;
}

export const Image = styled.img<ImageProps>`
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;

  ${({ $contain }) =>
    $contain &&
    css`
      object-fit: contain;
    `}
`;

interface WrapperProps {
  $size?: 'large' | 'small' | 'fullWidth';
  $tone?:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'error'
    | 'errorStrong'
    | 'info'
    | 'neutral'
    | 'transparent'
    | 'outline';
}

export const Wrapper = styled.div<WrapperProps>`
  flex-shrink: 0;
  position: relative;
  border-radius: var(--media-preview-border-radius, ${size(1)});
  width: var(--media-preview-size);
  height: var(--media-preview-height, var(--media-preview-size));
  background: var(--media-preview-bg);
  color: var(--media-preview-fg);

  ${({ $size }) =>
    $size === 'large'
      ? css`
          --media-preview-size: ${size(15)};
          --media-preview-icon-size: ${size(4)};
        `
      : $size === 'small'
        ? css`
            --media-preview-size: ${size(5)};
            --media-preview-icon-size: ${size(2)};
            --media-preview-border-radius: ${size(0.5)};
          `
        : $size === 'fullWidth'
          ? css`
              --media-preview-size: 100%;
              --media-preview-height: 0;
              --media-preview-icon-size: ${size(3)};
              padding-bottom: 100%;

              ${Image} {
                position: absolute;
                top: 0;
                left: 0;
              }
            `
          : css`
              --media-preview-size: ${size(8)};
              --media-preview-icon-size: ${size(3)};
            `}

  ${({ $tone }) =>
    $tone === 'primary'
      ? css`
          --media-preview-bg: var(--c-primary-bg);
          --media-preview-fg: var(--c-primary-fg);
        `
      : $tone === 'secondary'
        ? css`
            --media-preview-bg: var(--c-secondary-bg);
            --media-preview-fg: var(--c-secondary-fg);
          `
        : $tone === 'success'
          ? css`
              --media-preview-bg: var(--c-semantic-success-300);
              --media-preview-fg: var(--c-semantic-success-700);
            `
          : $tone === 'warning'
            ? css`
                --media-preview-bg: var(--c-semantic-warning-300);
                --media-preview-fg: var(--c-semantic-warning-700);
              `
            : $tone === 'error'
              ? css`
                  --media-preview-bg: var(--c-semantic-error-300);
                  --media-preview-fg: var(--c-semantic-error-700);
                `
              : $tone === 'errorStrong'
                ? css`
                    --media-preview-bg: var(--c-semantic-error-500);
                    --media-preview-fg: var(--c-semantic-error-100);
                  `
                : $tone === 'info'
                  ? css`
                      --media-preview-bg: var(--c-semantic-info-300);
                      --media-preview-fg: var(--c-semantic-info-700);
                    `
                  : $tone === 'neutral'
                    ? css`
                        --media-preview-bg: var(--c-accent-bg);
                        --media-preview-fg: var(--c-accent-fg);
                      `
                    : $tone === 'transparent'
                      ? css`
                          --media-preview-bg: repeating-conic-gradient(
                              var(--c-transparent-chess-pattern) 0% 25%,
                              transparent 0% 50%
                            )
                            50% / ${size(1)} ${size(1)}; // Transparent chess pattern;
                          --media-preview-fg: var(--c-text-main);
                        `
                      : $tone === 'outline'
                        ? css`
                            --media-preview-bg: transparent;
                            --media-preview-fg: var(--c-text-main);

                            border: 1px solid var(--c-border-main);
                          `
                        : undefined}
`;

export const Count = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  padding: ${size(0.25)} ${size(0.5)};
  font-size: ${(props) => props.theme.font.size.xsmall};
  background-color: rgba(var(--c-overlay-rgb), 0.2);
  color: var(--c-base-fg);
  border-bottom-right-radius: inherit;
`;

export const Icon = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: var(--media-preview-icon-size);
`;

export const Branding = styled.img`
  position: absolute;
  top: 0;
  right: 0;
  transform: translateX(50%);
  display: block;
  width: ${size(2)};
  border-radius: ${size(0.75)};
  outline: 2px solid var(--c-bg-main);
`;

export const Notification = styled.div`
  position: absolute;
  top: 0;
  right: ${size(-1)};
  display: block;

  > * {
    outline: 2px solid var(--c-bg-main);
  }
`;
